import React from 'react';
import styled from 'styled-components';

export const DiabetesPageTemplate = ({
  eyebrow = 'Diabetes Care',
  title,
  intro,
  heroImage,
  heroAlt,
  heroSummary,
  sections = [],
  faqItems = [],
}) => {
  return (
    <PageShell>
      <ContentWrap>
        <HeroCard>
          <HeroCopy>
            <Eyebrow>{eyebrow}</Eyebrow>
            <HeroTitle>{title}</HeroTitle>
            <HeroIntro>{intro}</HeroIntro>
            {heroSummary ? <HeroSummary>{heroSummary}</HeroSummary> : null}
          </HeroCopy>
          <HeroMedia>
            <HeroImage src={heroImage} alt={heroAlt} />
          </HeroMedia>
        </HeroCard>

        {sections.map((section) => (
          <SectionCard key={section.title}>
            <SectionHeader>
              <SectionTitle>{section.title}</SectionTitle>
              {section.kicker ? <SectionKicker>{section.kicker}</SectionKicker> : null}
            </SectionHeader>

            <SectionGrid $hasImage={Boolean(section.image)}>
              <SectionBody>
                {section.paragraphs?.map((paragraph) => (
                  <BodyText key={paragraph}>{paragraph}</BodyText>
                ))}

                {section.columns ? (
                  <InfoGrid>
                    {section.columns.map((column) => (
                      <InfoCard key={column.title}>
                        <InfoCardTitle>{column.title}</InfoCardTitle>
                        {column.text ? <CardText>{column.text}</CardText> : null}
                        {column.items ? (
                          <List>
                            {column.items.map((item) => (
                              <ListItem key={item}>{item}</ListItem>
                            ))}
                          </List>
                        ) : null}
                      </InfoCard>
                    ))}
                  </InfoGrid>
                ) : null}

                {section.items ? (
                  <List>
                    {section.items.map((item) => (
                      <ListItem key={item}>{item}</ListItem>
                    ))}
                  </List>
                ) : null}

                {section.faqs ? (
                  <FaqGrid>
                    {section.faqs.map((faq) => (
                      <FaqCard key={faq.question}>
                        <FaqQuestion>{faq.question}</FaqQuestion>
                        <FaqAnswer>{faq.answer}</FaqAnswer>
                      </FaqCard>
                    ))}
                  </FaqGrid>
                ) : null}

                {section.note ? <NoteBox>{section.note}</NoteBox> : null}
              </SectionBody>

              {section.image ? (
                <SupportMedia>
                  <SupportImage src={section.image} alt={section.imageAlt || section.title} />
                  {section.imageCaption ? <ImageCaption>{section.imageCaption}</ImageCaption> : null}
                </SupportMedia>
              ) : null}
            </SectionGrid>
          </SectionCard>
        ))}

        {faqItems.length ? (
          <SectionCard>
            <SectionHeader>
              <SectionTitle>Frequently Asked Questions</SectionTitle>
            </SectionHeader>
            <FaqGrid>
              {faqItems.map((faq) => (
                <FaqCard key={faq.question}>
                  <FaqQuestion>{faq.question}</FaqQuestion>
                  <FaqAnswer>{faq.answer}</FaqAnswer>
                </FaqCard>
              ))}
            </FaqGrid>
          </SectionCard>
        ) : null}
      </ContentWrap>
    </PageShell>
  );
};

const PageShell = styled.section`
  width: 100%;
  padding: 40px 18px 88px;
  background:
    radial-gradient(circle at top left, rgba(11, 94, 126, 0.09), transparent 30%),
    linear-gradient(180deg, #f2f7fb 0%, #ffffff 28%, #f8fbfd 100%);

  @media (max-width: 768px) {
    padding: 28px 14px 72px;
  }
`;

const ContentWrap = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  display: grid;
  gap: 28px;
  color: #2b3440;
`;

const HeroCard = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
  gap: 28px;
  align-items: stretch;
  padding: 30px;
  border-radius: 32px;
  background: linear-gradient(135deg, #eff8fb 0%, #ffffff 52%, #eef5f2 100%);
  border: 1px solid rgba(52, 92, 112, 0.12);
  box-shadow: 0 22px 50px rgba(20, 47, 66, 0.1);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 22px;
  }
`;

const HeroCopy = styled.div`
  align-self: center;
`;

const Eyebrow = styled.p`
  margin: 0 0 10px;
  color: #0e7184;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  margin: 0 0 18px;
  color: #21303d;
  font-size: clamp(2.4rem, 4vw, 4.5rem);
  line-height: 1.05;
  font-weight: 700;
  font-family: Georgia, 'Times New Roman', serif;
`;

const HeroIntro = styled.p`
  margin: 0 0 18px;
  max-width: 680px;
  color: #425161;
  font-size: clamp(1.03rem, 1.3vw, 1.16rem);
  line-height: 1.8;
`;

const HeroSummary = styled.div`
  display: inline-flex;
  padding: 12px 18px;
  border-radius: 999px;
  background: rgba(14, 113, 132, 0.1);
  color: #1d6171;
  font-size: 0.96rem;
  font-weight: 600;
  line-height: 1.5;
`;

const HeroMedia = styled.div`
  min-height: 360px;
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  border-radius: 26px;
  box-shadow: 0 16px 36px rgba(28, 50, 67, 0.15);
`;

const SectionCard = styled.section`
  padding: 28px;
  border-radius: 28px;
  background: #ffffff;
  border: 1px solid rgba(52, 92, 112, 0.1);
  box-shadow: 0 16px 38px rgba(20, 47, 66, 0.08);

  @media (max-width: 768px) {
    padding: 22px 18px;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #22313e;
  font-size: clamp(1.7rem, 2.5vw, 2.7rem);
  line-height: 1.15;
  font-weight: 700;
  font-family: Georgia, 'Times New Roman', serif;
`;

const SectionKicker = styled.p`
  margin: 10px 0 0;
  color: #0e7184;
  font-size: 0.95rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $hasImage }) => ($hasImage ? 'minmax(0, 1.35fr) minmax(260px, 0.75fr)' : '1fr')};
  gap: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SectionBody = styled.div``;

const BodyText = styled.p`
  margin: 0 0 18px;
  color: #4c5a68;
  font-size: clamp(1rem, 1.25vw, 1.12rem);
  line-height: 1.82;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-top: 8px;
`;

const InfoCard = styled.article`
  padding: 18px;
  border-radius: 20px;
  background: linear-gradient(180deg, #f6fbfd 0%, #f9fcfb 100%);
  border: 1px solid rgba(14, 113, 132, 0.12);
`;

const InfoCardTitle = styled.h3`
  margin: 0 0 10px;
  color: #244453;
  font-size: 1.15rem;
  line-height: 1.35;
  font-weight: 700;
`;

const CardText = styled.p`
  margin: 0;
  color: #50606e;
  font-size: 0.98rem;
  line-height: 1.72;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 12px;
`;

const ListItem = styled.li`
  position: relative;
  padding-left: 22px;
  color: #4c5a68;
  font-size: clamp(0.99rem, 1.2vw, 1.08rem);
  line-height: 1.75;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.72em;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(135deg, #0e7184, #7ab773);
    transform: translateY(-50%);
  }
`;

const SupportMedia = styled.figure`
  margin: 0;
  padding: 14px;
  border-radius: 24px;
  background: linear-gradient(180deg, #f3f8fb 0%, #ffffff 100%);
  border: 1px solid rgba(52, 92, 112, 0.08);
`;

const SupportImage = styled.img`
  width: 100%;
  display: block;
  border-radius: 18px;
  object-fit: cover;
  min-height: 260px;
  max-height: 420px;
`;

const ImageCaption = styled.figcaption`
  margin-top: 12px;
  color: #5f6d79;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const NoteBox = styled.div`
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(14, 113, 132, 0.08), rgba(122, 183, 115, 0.08));
  color: #35515d;
  font-size: 0.98rem;
  line-height: 1.72;
  border-left: 4px solid #0e7184;
`;

const FaqGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
`;

const FaqCard = styled.article`
  padding: 20px;
  border-radius: 22px;
  background: linear-gradient(180deg, #fbfefe 0%, #f5fafb 100%);
  border: 1px solid rgba(14, 113, 132, 0.12);
  box-shadow: 0 10px 22px rgba(20, 47, 66, 0.06);
`;

const FaqQuestion = styled.h3`
  margin: 0 0 10px;
  color: #22313e;
  font-size: 1.08rem;
  line-height: 1.45;
  font-weight: 700;
`;

const FaqAnswer = styled.p`
  margin: 0;
  color: #52606d;
  font-size: 0.98rem;
  line-height: 1.75;
`;
